import PhoneInput from 'components/Common/Form/Input/PhoneInput'
import TextInput from 'components/Common/Form/Input/TextInput'
import { useAppSelector } from 'hooks/reduxHooks'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import { getLastUsedLoginMethodLabel } from 'lib/auth/lastUsedLoginMethod'

const Container = styled.div`
  position: relative;
`

const LastUsedTag = styled(StatusLabel)`
  display: inline-flex;
  align-items: center;
  margin-inline-start: ${rem(8)};
  pointer-events: none;
`

const SwappablePhoneInput = styled(PhoneInput)`
  && > * {
    transition: none;
  }

  &[hidden] {
    display: none;
  }
`

const SwappableEmailInput = styled(TextInput)`
  && > * {
    transition: none;
  }

  &[hidden] {
    display: none;
  }
`

interface Props {
  defaultEmail?: string
  /** Action centered beside the control on desktop; stacked below help/errors on smaller screens. */
  action?: React.ReactElement
  phoneInputName?: string
  emailInputName?: string
  className?: string
  loginMethod?: 'phone'
  noValidationSpacing?: boolean
  autoFocus?: boolean
  showLastUsedTag?: boolean
}

function AccountAccessUserInput(props: Props) {
  const {
    defaultEmail,
    emailInputName = 'email',
    action,
    phoneInputName = 'phoneNumber',
    className,
    loginMethod,
    noValidationSpacing,
    autoFocus = true,
    showLastUsedTag = false,
  } = props

  const intl = useIntl()
  const [value, setValue] = useState<string>(defaultEmail ?? '')
  const emailInputRef = useRef<HTMLInputElement>(null)
  const phoneInputRef = useRef<HTMLInputElement>(null)
  const isMobile = useScreenSizeOnly('mobile')
  const headlessMode = useAppSelector(state => state.system.headlessMode)
  const allowPhoneNumberLogin = useAppSelector(state => !!state.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.allowPhoneNumberLogin])
  const [anyInputFocus, setAnyInputFocus] = useState<boolean>(false)
  const onTextChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>((e) => {
    setValue(e.currentTarget.value)
  }, [])

  useEffect(() => {
    if (!headlessMode && !isMobile && autoFocus) {
      if (loginMethod === 'phone') {
        phoneInputRef.current?.focus()
      } else {
        emailInputRef.current?.focus()
      }
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onFocusAnyInput = useCallback(() => {
    setAnyInputFocus(true)
  }, [])

  const isPossiblyPhoneNumber = useMemo(() => {
    return allowPhoneNumberLogin && /^(\d|\+)\d*$/.test(value)
  }, [allowPhoneNumberLogin, value])

  useLayoutEffect(() => {
    if (!anyInputFocus) return
    if (isPossiblyPhoneNumber) {
      phoneInputRef.current?.focus()
    } else {
      emailInputRef.current?.focus()
    }
  }, [anyInputFocus, isPossiblyPhoneNumber])

  const emailAddressLabel = intl.formatMessage({
    id: '_1lIDHzvg9xT_xNcZ0-P',
    defaultMessage: 'Email address',
  })

  const emailPlaceholder = intl.formatMessage({
    id: 'QeGn0wUce6vVjaxzoAgz',
    defaultMessage: 'Email',
  })

  const emailRequired = intl.formatMessage({
    id: '2M744UrPgy3eL9r5c6Sc',
    defaultMessage: 'Email is required',
  })

  const helpText = intl.formatMessage({
    id: 'ILE79Vi9tqLMWVYXf5B7',
    defaultMessage: 'We’ll check if you have an account with us or if we need to create one for you.',
  })

  const emailOrPhoneLabel = intl.formatMessage({
    id: 'okCxeCsCtFqNuya6W2Zj',
    defaultMessage: 'Email address or phone number',
  })

  const emailOrPhonePlaceholder = intl.formatMessage({
    id: 'Ipb2r9z3hsCO5Fzyr9V-',
    defaultMessage: 'Email or phone number',
  })

  const emailOrPhoneRequired = intl.formatMessage({
    id: 'p0qlFLENrt2T56q9M14q',
    defaultMessage: 'Email or phone number is required',
  })

  const phoneNumberLabel = intl.formatMessage({
    id: 'T_4RhGB0-sNJgnkOJhK2',
    defaultMessage: 'Phone number',
  })

  const phoneNumberRequired = intl.formatMessage({
    id: 'E_ISwBkkYKIONy4w6BAN',
    defaultMessage: 'Phone number is required',
  })

  const lastUsedLabel = getLastUsedLoginMethodLabel(intl)

  if (loginMethod === 'phone') {
    return <PhoneInput
      action={action}
      inputRef={phoneInputRef}
      label={phoneNumberLabel}
      name={phoneInputName}
      placeholder={phoneNumberLabel}
      requiredErrorMessage={phoneNumberRequired}
      required
      phoneNumber={value}
      onNumberChange={onTextChange}
      className={className}
      noValidationSpacing={noValidationSpacing}
      helpText={helpText}
    />
  }

  if (!allowPhoneNumberLogin) {
    return <TextInput
      defaultValue={defaultEmail}
      action={action}
      label={emailAddressLabel}
      id="email"
      required
      type="email"
      name="email"
      autoComplete="email"
      placeholder={emailPlaceholder}
      requiredErrorMessage={emailRequired}
      ref={emailInputRef}
      helpText={helpText}
      className={className}
      noValidationSpacing={noValidationSpacing}
    />
  }

  const label = showLastUsedTag ? <>
    {emailOrPhoneLabel}
    <LastUsedTag variant="confirmation">{lastUsedLabel}</LastUsedTag>
  </> : emailOrPhoneLabel

  return <Container className={className}>
    <SwappableEmailInput
      action={isPossiblyPhoneNumber ? undefined : action}
      label={label}
      id="email"
      required
      type="email"
      name={emailInputName}
      autoComplete="email"
      placeholder={emailOrPhonePlaceholder}
      requiredErrorMessage={emailOrPhoneRequired}
      ref={emailInputRef}
      value={value}
      onChange={onTextChange}
      helpText={helpText}
      hidden={isPossiblyPhoneNumber}
      disabled={isPossiblyPhoneNumber}
      onFocus={onFocusAnyInput}
      noValidationSpacing={noValidationSpacing}
    />
    <SwappablePhoneInput
      action={isPossiblyPhoneNumber ? action : undefined}
      inputRef={phoneInputRef}
      label={emailOrPhoneLabel}
      name={phoneInputName}
      placeholder={emailOrPhonePlaceholder}
      requiredErrorMessage={emailOrPhoneRequired}
      required
      phoneNumber={value}
      onNumberChange={onTextChange}
      hidden={!isPossiblyPhoneNumber}
      disabled={!isPossiblyPhoneNumber}
      onFocus={onFocusAnyInput}
      noValidationSpacing={noValidationSpacing}
      helpText={helpText}
    />
  </Container>
}

export default AccountAccessUserInput
